// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

// SyncFusion
import { GridComponent, NotifyArgs, PredicateModel } from '@syncfusion/ej2-angular-grids';

// Cerium
import { GridDesign } from './cer-grid-design';
import { CerGridComponent } from './cer-grid.component';
import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { CerMetaUserView } from '../cer-form/cer-form-persistence.service';
import { CerGridFilterService } from './cer-grid-filter.service';
import { CerGridCommandService } from './cer-grid-command.service';
import { uiCommandPersistenceComplete, uiCommandPersistenceStandard } from '@ui/cer-ui-commands';

@Injectable()
export class CerGridPersistenceService implements OnDestroy {
  private subscriptionManager: Subscription = new Subscription();

  // Grid
  public appGrid: CerGridComponent;
  public grid: GridComponent;
  public design: GridDesign;
  private localDataEnabled: boolean = false;

  // Constructor
  constructor(
    private filterService: CerGridFilterService,
    private commandService: CerGridCommandService) {
  }

  // Subscriptions
  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  private onCommandPre(event: UiCommandEvent) {
    if (event.cancel) {
      return;
    }
    if (event.source == UiCommandSourceEnum.ActionBegin) {
      switch (event.actionType) {
        case UiActionTypeEnum.DataBound:
          if (this.localDataEnabled && event.args.actionArgs?.requestType == UiActionTypeEnum.RefreshPersistence) {
            var cmd = this.appGrid.commandService;
            cmd.dataSourceSetOffline(false);
            cmd.dataSourceQueryClone();
            if (this.grid.getDataRows().length == 0) {
              this.gridRefreshRequest();
            }
            this.localDataEnabled = false;
          }
          break;
      }
    }
    switch (event.commandId) {
      case uiCommandPersistenceComplete.id:
        this.appGrid.routeService.userViewTypeSelect('complete');
        break;
      case uiCommandPersistenceStandard.id:
        this.appGrid.routeService.userViewTypeSelect('standard');
        break;
    }
  }

  public init(appGrid: CerGridComponent, design: GridDesign) {
    this.appGrid = appGrid;
    this.grid = appGrid.grid;
    this.design = design;
    this.subscriptionManager.add(
      this.appGrid.commandService.commandPre$.subscribe(cmd => this.onCommandPre(cmd))
    );
    this.subscriptionManager.add(
      this.appGrid.dataService.fkDataLoaded$.subscribe(fkDataloaded => this.onFKDataLoaded(fkDataloaded))
    )
  }

  public defaultPersistenceObject: any = null;
  public completePersistenceObject: any = null;
  public saveDefault() {
    this.defaultPersistenceObject = this.get();
    this.completePersistenceObject = this.get();
    var columns: any[] = this.completePersistenceObject.grid?.columns;
    if (columns != null) {
      columns.forEach((c: any) => c.visible = true);
    }
  }

  public apply(view: CerMetaUserView, persistenceObject: any = null): boolean {
    this.commandService.cmdDispatchActionBegin(UiActionTypeEnum.PersistenceApply, view);
    switch (view.type) {
      case 'complete':
        persistenceObject = this.completePersistenceObject;
        break;
      case 'standard':
        persistenceObject = this.defaultPersistenceObject;
        break;
    }
    return this.applyGridPersistenceObject(persistenceObject);
  }

  /*
          var completeColumns : any[] = [];
        this.grid.columns.forEach(c => {
          var field : string = (<any> c).field;
          if (field?.length > 0) {
            completeColumns.push({field: field, visible: true});
          }
        }); // Show all columns
        if (!this.applyGridPersistenceColumns(completeColumns, [], false)) {
          this.applyGridPersistenceColumns(completeColumns, [], true)
        }
        return this.applyRefresh(true);
        private applyPersistence(viewType: CerFormUserViewType, persistenceObject: any): boolean {
    if (viewType == 'custom') {
      if (persistenceObject && persistenceObject['grid'] && this.grid) {
        return this.applyGridPersistenceObject(persistenceObject['grid']);
      }
    }
    else {
      this.appGrid.selection.rowSelectedKeep();
      var changed = this.applyColumnsStandardView(viewType == 'complete');
      return this.applyRefresh(changed);
    }
    return false;
  }

  */
  public getId(): string {
    var id: string = null;

    id = 'grid';
    //if (this.design?.dataParent || this.design?.dataChild) {
    //  id = id + "-dataParent-" + this.design.dataChild;
    //}
    var subId = this.design?.id;
    if (subId && typeof subId === "string") {
      id = id + "-id-" + subId;
    }
    return id;
  }

  public get(): any {
    var o: any = null;
    if (this.grid) {
      o = new Object();
      o['grid'] = JSON.parse(this.getData());
    }
    return o;
  }

  public applyComplete(view: CerMetaUserView) {
    if (this.design?.dataChild === true) {
      this.gridRefreshRequest();
    }
    this.commandService.cmdDispatchActionComplete(UiActionTypeEnum.PersistenceApply, view);
  }

  private getData() {
    var keyEntity: string[] = ['sortSettings', 'filterSettings', 'groupSettings', 'columns', 'searchSettings'];
    var includeProperties: any = {
      sortSettings: ['propName', 'columns', 'direction', 'field', 'isFromGroup'],
      filterSettings: ['propName', 'columns', 'field', 'operator', 'value', 'predicate', 'type', 'matchCase', 'ignoreAccent', 'actualfilterValue', 'actualOperator', 'isForeignKeyFilter'],
      groupSettings: ['propName', 'columns', 'field'],
      columns: ['propName', 'columns', 'field', 'headerText', 'autofit', 'visible', 'width'],
      searchSettings: []
    };

    var persistedObject: any = {};
    keyEntity.forEach(key => {
      var currentObjectProp = (<any>this.grid)[key];
      let persistedObjectProp: object = this.copyObjectData(currentObjectProp, includeProperties[key]);
      persistedObject[key] = persistedObjectProp;
    });
    persistedObject.dataUrl = this.filterService.filterUrlLatest; // Url of last request

    return JSON.stringify(persistedObject);
  }

  private copyObjectData(from: object, includes: string[]): object {
    if (!from) {
      return from; // null, undefined values check
    }
    var isArray: boolean = Array.isArray(from);
    var to: any = isArray ? [] : {};
    if (isArray) {
      (<any[]>from).forEach((value: any) => {
        to.push((typeof value === "object") ? this.copyObjectData(value, includes) : value);
      });
    }
    else {
      for (const key in from) {
        if (!includes || includes.includes(key)) {
          var keyFrom = <keyof typeof from>key;
          var value: any = from[keyFrom];
          if (value != from) {
            var keyTo = <keyof typeof to>key;
            to[keyTo] = (typeof value === "object" && !(value instanceof Date)) ? this.copyObjectData(value, includes) : value;
          }
        }
      }
    }
    return to;
  }

  private fkDataLoaded: boolean = false;
  private onFKDataLoaded(fkDataLoaded: boolean): void {
    this.fkDataLoaded = fkDataLoaded;
    this.gridRefreshTrigger();
  }

  private gridRefreshRequested: boolean = false;
  private gridRefreshRequest() {
    this.gridRefreshRequested = true;
    this.gridRefreshTrigger();
  }

  private gridRefreshTrigger() {
    if (this.gridRefreshRequested && this.fkDataLoaded && this.appGrid?.grid?.renderModule) {
      this.gridRefreshRequested = false;
      this.gridRefresh();
    }
  }

  private gridRefresh() {
    var e: any = { requestType: UiActionTypeEnum.RefreshPersistence };
    this.appGrid.grid.renderModule.refresh(<NotifyArgs>e);
    /*
      this.appGrid.grid.renderModule.refresh(e);
    this.appGrid.commandService.refreshColumns();
    this.appGrid.grid.enableAltRow = !this.appGrid.grid.enableAltRow;
    this.appGrid.grid.enableAltRow = !this.appGrid.grid.enableAltRow;
    //this.gridService.refreshColumns();
    */
  }

  private applyGridPersistenceObject(persistenceObject: any): boolean {
    if (!persistenceObject?.grid) {
      return false;
    }

    var obj: any = persistenceObject?.grid;

    //var isGridDataBoundSave = this.isGridDataBound;
    //this.isGridDataBound = false;
    var columns = obj["columns"];
    var filterColumns = obj['filterSettings']?.['columns'];
    var sortColumns = obj['sortSettings']?.['columns'];
    var groupColumns = obj['groupSettings']?.['columns'];

    var dataBindOnChange: boolean = false;
    var columnChange = this.applyGridPersistenceColumns(columns ?? [], filterColumns ?? [], dataBindOnChange);
    var sortChange = this.applyGridPersistenceSortColumns(sortColumns ?? [], dataBindOnChange);
    var groupChange = this.applyGridPersistenceGroupColumns(groupColumns ?? [], dataBindOnChange);
    var filterChange = this.applyGridPersistenceFilterColumns(filterColumns ?? [], dataBindOnChange);

    var needRefresh = (sortChange || groupChange || columnChange || filterChange);

    //var dataManager = this.grid.getDataModule().dataManager;

    this.localDataEnabled = (needRefresh && this.grid.renderModule?.data != null);
    if (this.localDataEnabled) {
      this.appGrid.commandService.dataSourceSetOffline(true);
    }

    dataBindOnChange = true;
    if (columnChange) {
      this.applyGridPersistenceColumns(columns ?? [], filterColumns ?? [], dataBindOnChange);
      //this.appGrid.commandService.refreshColumns();
    }
    if (sortChange) {
      this.applyGridPersistenceSortColumns(sortColumns ?? [], dataBindOnChange);
    }
    if (groupChange) {
      this.applyGridPersistenceGroupColumns(groupColumns ?? [], dataBindOnChange);
    }

    if (filterChange) {
      this.applyGridPersistenceFilterColumns(filterColumns ?? [], dataBindOnChange);
    }
    if (this.localDataEnabled) {
      // this.appGrid.commandService.dataSourceInit();
    }
    needRefresh = this.applyRefresh(needRefresh);

    return needRefresh;
  }

  private applyRefresh(needRefresh: boolean): boolean {
    if (needRefresh && this.design?.dataChild != true) {
      this.gridRefreshRequest();
      return false;
    }
    return needRefresh;
  }

  private applyGridPersistenceColumns(columns: any[], persitencefilterColumns: any[], dataBindOnChange: boolean): boolean {
    var grid = this.grid;
    var changed: boolean = false;

    if (grid.allowReordering && columns && columns.length > 0) {
      var indexMax = columns.length - 1;
      var hasMore: boolean;
      do {
        hasMore = false;
        for (var index: number = 0; index <= indexMax; index++) {
          var c = columns[index];
          if (c) {
            var col = grid.getColumnByField(c.field);
            if (col != null && index >= 0 && grid.getColumnByIndex(index) != col) {
              if (dataBindOnChange) {
                (<any>grid.reorderModule).moveColumns(index, col, false, false);
                hasMore = true;
              }
              changed = true;
              index = indexMax + 1; // Break loop
            }
            if (col == null) {
              console.log("Unknown column: "+c.field)
            }
            else {
              if (col.visible != c.visible ||
                (col.width != c.width && c.width != null)) {
                col.visible = c.visible;
                if (c.width != null) {
                  col.width = c.width;
                }
                changed = true;
              }
              if (persitencefilterColumns) {
                // Update filter uid
                persitencefilterColumns.filter(fc => fc.field == col.field && col.uid).forEach(fc => fc.uid = col.uid);
              }
            }
          }
        }
      } while (hasMore);
      if (changed && this.grid?.headerModule) {
        this.grid.headerModule.refreshUI();
      }
    }
    return changed;
  }

  private applyGridPersistenceFilterColumns(columns: any[], dataBindOnChange: boolean): boolean {
    var changed = false;
    var grid = this.grid;

    if (grid.filterSettings.columns?.length != columns.length || columns.length > 0) {
      changed = true;
    }
    if (changed && dataBindOnChange) {

      var predicates: PredicateModel[] = [];

      if (columns && columns.length > 0) {
        var fieldPredicates: Map<string, any[]> = new Map<string, any[]>();
        columns.forEach(c => {
          if (!fieldPredicates.has(c.field)) {
            fieldPredicates.set(c.field, [c])
          }
          else {
            var arr: any[] = fieldPredicates.get(c.field);
            arr.push(c);
            fieldPredicates.delete(c.field);
            fieldPredicates.set(c.field, arr);
          }
        });

        fieldPredicates.forEach((cols: any[], field: string) => {
          var col = grid.getColumnByField(field);
          if (col) {
            //var value: any = cols.length > 1 ? cols.map(c => c.value) : cols[0].value;
            var firstValue: boolean = true;
            var c = cols[0];
            cols.forEach(cValue => {
              predicates.push({
                field: c.field,
                matchCase: c.matchCase,
                ignoreAccent: c.ignoreAccent,
                operator: c.operator,
                predicate: firstValue ? 'and' : 'or',
                //value: value,
                value: cValue.value,
                type: c.type,
                uid: c.uid
              });
              firstValue = false;
              changed = true;
            });
          }
        });
      }
      this.grid.filterSettings.columns = predicates;
      changed = true;

    }
    return changed;
  }

  private applyGridPersistenceSortColumns(columns: any[], dataBindOnchange: boolean): boolean {
    var changed = false;
    var grid = this.grid;

    // Check if change to sorting is needed
    if (grid.sortSettings.columns.length != columns.length) {
      changed = true;
    }
    else {
      columns.forEach((col, index) => {
        var colOrig = grid.sortSettings.columns[index];
        if (colOrig) {
          if (col.field != colOrig.field || col.direction != colOrig.direction) {
            changed = true;
          }
        }
      });
    }

    if (changed && dataBindOnchange) {
      grid.sortSettings.columns = columns;
    }
    return changed;

    /*
    var multiSort: boolean = false;
    var sortColumns: SortDescriptorModel[] = [];
    columns.forEach(c => {
      var col = grid.getColumnByField(c.field);
      if (col && col.allowSorting) {
        if (dataBindOnchange) {
          this.grid.sortColumn(c.field, c.direction, multiSort);
          multiSort = true;
        }
        else {
          sortColumns.push({ field: col.field, direction: c.direction });
        }
        changed = true;
      }
    });
    if (!dataBindOnchange) {
      this.grid.sortSettings.columns = sortColumns;
    }
      */
  }

  private applyGridPersistenceGroupColumns(columns: any[], dataBindOnChange: boolean): boolean {
    var changed = false;
    var groupSettings = this.grid.groupSettings;

    if (groupSettings.columns.length != columns.length) {
      changed = true;
    }
    else {
      columns.forEach((c, index) => {
        var col = this.grid.getColumnByField(c);
        if (!col || col.field != groupSettings.columns[index]) {
          changed = true;
        }
      });
    }

    if (changed && dataBindOnChange) {
      this.grid.groupSettings.columns = columns;
      changed = true;
    }
    return changed;
  }
}